import React, { useState, useEffect } from 'react';
import {
  Form,
  Select,
  Card,
  Row,
  Col,
  Input,
  Switch,
  Button,
  Space,
  Table,
  Modal,
  message,
  Tag,
  Alert
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import { gqlClient } from '../../../helpers/gqlProvider';
import { SUPPLIER_PORTAL_TEMPLATES_QUERY } from '../../sourcing/queries';

const { Option } = Select;
const { TextArea } = Input;

interface Field {
  name: string;
  label: string;
  description: string;
  type: string;
  isRequired: boolean;
}

interface FileRequirement {
  name: string;
  label: string;
  description: string;
  documentType: string;
  isRequired: boolean;
}

interface CustomField {
  name: string;
  label: string;
  type: string;
  isRequired: boolean;
}

interface Template {
  id: string;
  name: string;
  sourcingType: 'rfq' | 'rfp';
  biddingType: 'ncb' | 'icb';
  fields: Field[];
  fileRequirements: FileRequirement[];
}

interface ConfigureTabProps {
  form: any;
  formData: any;
  sourcingData: any;
  updateFormData?: (data: any) => void;
  isPublishing?: boolean;
}

export const ConfigureTab: React.FC<ConfigureTabProps> = ({
  form,
  formData,
  sourcingData,
  updateFormData,
  isPublishing = false,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [customFieldModalVisible, setCustomFieldModalVisible] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState<{ field: CustomField; index: number } | null>(null);
  const [customFieldForm] = Form.useForm();

  const [fileRequirementModalVisible, setFileRequirementModalVisible] = useState(false);
  const [editingFileRequirement, setEditingFileRequirement] = useState<{ requirement: FileRequirement; index: number } | null>(null);
  const [fileRequirementForm] = Form.useForm();

  // Force re-render when form data changes
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await gqlClient.query(SUPPLIER_PORTAL_TEMPLATES_QUERY, {});
        if (response.data?.supplierPortalTemplates) {
          setTemplates(response.data.supplierPortalTemplates);
        } else {
          console.error('No templates data received:', response);
          message.error('Failed to load sourcing templates. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        message.error('Failed to load sourcing templates. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Initialize form data when component mounts or formData changes
  useEffect(() => {
    console.log('ConfigureTab: formData changed:', formData);
    if (formData && Object.keys(formData).length > 0) {
      // Restore form data when component remounts
      const formValues = {
        templateId: formData.templateId,
        sourcingType: formData.sourcingType,
        biddingType: formData.biddingType,
        title: isPublishing ? sourcingData?.title : (formData.title || sourcingData?.title),
        description: isPublishing ? sourcingData?.description : (formData.description || sourcingData?.description),
        fields: formData.fields || [],
        fileRequirements: formData.fileRequirements || [],
      };

      console.log('ConfigureTab: Restoring form values:', formValues);
      form.setFieldsValue(formValues);

      // Update selected template if templateId exists
      if (formData.templateId) {
        const template = templates.find(t => t.id === formData.templateId);
        if (template) {
          setSelectedTemplate(template);
        }
      }
    } else if (isPublishing && sourcingData) {
      // If no formData but isPublishing, initialize with sourcing data
      const formValues = {
        title: sourcingData.title,
        description: sourcingData.description,
        fields: [],
        fileRequirements: [],
      };

      console.log('ConfigureTab: Initializing with sourcing data:', formValues);
      form.setFieldsValue(formValues);
    }
  }, [formData, form, templates, isPublishing, sourcingData]);

  // Debug: Log when component renders
  useEffect(() => {
    console.log('ConfigureTab: Component rendered, formData:', formData);
    console.log('ConfigureTab: Current form values:', form.getFieldsValue());
  });

  // Force update component state when form data is restored
  useEffect(() => {
    if (formData && formData.fields && formData.fields.length > 0) {
      console.log('ConfigureTab: Forcing refresh with fields:', formData.fields.length);
      setRefreshKey(prev => prev + 1);
    }
  }, [formData]);

  // Set selected template when templates are loaded and form has templateId
  useEffect(() => {
    if (templates.length > 0 && formData?.templateId) {
      const template = templates.find(t => t.id === formData.templateId);
      if (template && !selectedTemplate) {
        setSelectedTemplate(template);
      }
    }
  }, [templates, formData, selectedTemplate]);

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      const newFormData = {
        templateId: templateId,
        sourcingType: template.sourcingType,
        biddingType: template.biddingType,
        title: isPublishing ? sourcingData?.title : (formData.title || sourcingData?.title),
        description: isPublishing ? sourcingData?.description : (formData.description || sourcingData?.description),
        fields: [...template.fields], // Copy template fields to allow editing
        fileRequirements: [...template.fileRequirements], // Copy template fileRequirements to allow editing
      };
      form.setFieldsValue(newFormData);
      updateFormData?.(newFormData);
    }
  };

  const handleAddCustomField = () => {
    customFieldForm.resetFields();
    setEditingCustomField(null);
    setCustomFieldModalVisible(true);
  };


  const handleSaveCustomField = () => {
    customFieldForm.validateFields()
      .then((values: CustomField) => {
        const currentFields = form.getFieldValue('fields') || [];

        if (editingCustomField?.index !== undefined) {
          // Update existing field
          currentFields[editingCustomField.index] = values;
        } else {
          // Add new field
          currentFields.push(values);
        }

        form.setFieldsValue({ fields: currentFields });
        updateFormData?.({ fields: currentFields });
        setCustomFieldModalVisible(false);
        customFieldForm.resetFields();
        setEditingCustomField(null);
        message.success('Field saved successfully');
      })
      .catch((error) => {
        console.error('Validation error:', error);
      });
  };


  const handleEditField = (field: Field, index: number) => {
    customFieldForm.setFieldsValue(field);
    setEditingCustomField({ field, index });
    setCustomFieldModalVisible(true);
  };

  const handleDeleteField = (index: number) => {
    const currentFields = form.getFieldValue('fields') || [];
    currentFields.splice(index, 1);
    form.setFieldsValue({ fields: currentFields });
    updateFormData?.({ fields: currentFields });
    setRefreshKey(prev => prev + 1); // Force re-render
    message.success('Field deleted successfully');
  };

  const handleAddFileRequirement = () => {
    fileRequirementForm.resetFields();
    setEditingFileRequirement(null);
    setFileRequirementModalVisible(true);
  };

  const handleEditFileRequirement = (requirement: FileRequirement, index: number) => {
    fileRequirementForm.setFieldsValue(requirement);
    setEditingFileRequirement({ requirement, index });
    setFileRequirementModalVisible(true);
  };

  const handleSaveFileRequirement = () => {
    fileRequirementForm.validateFields()
      .then((values: FileRequirement) => {
        const currentFileRequirements = form.getFieldValue('fileRequirements') || [];

        if (editingFileRequirement?.index !== undefined) {
          // Update existing requirement
          currentFileRequirements[editingFileRequirement.index] = values;
        } else {
          // Add new requirement
          currentFileRequirements.push(values);
        }

        form.setFieldsValue({ fileRequirements: currentFileRequirements });
        updateFormData?.({ fileRequirements: currentFileRequirements });
        setFileRequirementModalVisible(false);
        fileRequirementForm.resetFields();
        setEditingFileRequirement(null);
        message.success('File requirement saved successfully');
      })
      .catch((error) => {
        console.error('Validation error:', error);
      });
  };

  const handleDeleteFileRequirement = (index: number) => {
    const currentFileRequirements = form.getFieldValue('fileRequirements') || [];
    currentFileRequirements.splice(index, 1);
    form.setFieldsValue({ fileRequirements: currentFileRequirements });
    updateFormData?.({ fileRequirements: currentFileRequirements });
    setRefreshKey(prev => prev + 1); // Force re-render
    message.success('File requirement deleted successfully');
  };




  return (
    <div>
      <Alert
        message="Configure Sourcing Template"
        description="Select a template and customize the fields and requirements for your sourcing event."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Row gutter={24}>
        <Col span={24}>
          <Card title="Template Selection" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Sourcing Template"
                  name="templateId"
                  rules={[{ required: true, message: 'Please select a template' }]}
                >
                  <Select
                    placeholder="Choose a sourcing template"
                    onChange={handleTemplateChange}
                    loading={loading}
                    disabled={loading}
                  >
                    {templates.map((template) => (
                      <Option key={template.id} value={template.id}>
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                            {template.name}  {template.sourcingType.toUpperCase()} - {template.biddingType.toUpperCase()}
                          </div>

                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Sourcing Type"
                  name="sourcingType"
                  rules={[{ required: true, message: 'Sourcing type is required' }]}
                >
                  <Select disabled>
                    <Option value="rfq">RFQ</Option>
                    <Option value="rfp">RFP</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Bidding Type"
                  name="biddingType"
                  rules={[{ required: true, message: 'Bidding type is required' }]}
                >
                  <Select disabled>
                    <Option value="ncb">NCB</Option>
                    <Option value="icb">ICB</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <Card title="Sourcing Details" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[{ required: true, message: 'Please enter sourcing title' }]}
                >
                  <Input
                    placeholder="Enter sourcing title"
                    disabled={isPublishing}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Description"
                  name="description"
                  rules={[{ required: true, message: 'Please enter sourcing description' }]}
                >
                  <TextArea
                    rows={3}
                    placeholder="Describe the sourcing opportunity..."
                    disabled={isPublishing}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {selectedTemplate && (
        <>
          <Row gutter={24}>
            <Col span={12}>
              <Card
                title="Fields"
                style={{ marginBottom: 24 }}
                extra={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddCustomField}
                    size="small"
                  >
                    Add Field
                  </Button>
                }
              >
                <Table
                  key={`fields-${refreshKey}`}
                  dataSource={form.getFieldValue('fields') || []}
                  columns={[
                    {
                      title: 'Field Name',
                      dataIndex: 'name',
                      key: 'name',
                    },
                    {
                      title: 'Label',
                      dataIndex: 'label',
                      key: 'label',
                    },
                    {
                      title: 'Type',
                      dataIndex: 'type',
                      key: 'type',
                      render: (type: string) => <Tag color="blue">{type}</Tag>,
                    },
                    {
                      title: 'Required',
                      dataIndex: 'isRequired',
                      key: 'isRequired',
                      render: (isRequired: boolean) => (
                        <Tag color={isRequired ? 'red' : 'green'}>
                          {isRequired ? 'Required' : 'Optional'}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: any, record: Field, index: number) => (
                        <Space>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditField(record, index)}
                          />
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteField(index)}
                          />
                        </Space>
                      ),
                    },
                  ]}
                  rowKey="name"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>

            <Col span={12}>
              <Card
                title="File Requirements"
                style={{ marginBottom: 24 }}
                extra={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddFileRequirement}
                    size="small"
                  >
                    Add File Requirement
                  </Button>
                }
              >
                <Table
                  key={`fileRequirements-${refreshKey}`}
                  dataSource={form.getFieldValue('fileRequirements') || []}
                  columns={[
                    {
                      title: 'Document Name',
                      dataIndex: 'name',
                      key: 'name',
                    },
                    {
                      title: 'Label',
                      dataIndex: 'label',
                      key: 'label',
                    },
                    {
                      title: 'Type',
                      dataIndex: 'documentType',
                      key: 'documentType',
                      render: (type: string) => <Tag color="purple">{type}</Tag>,
                    },
                    {
                      title: 'Required',
                      dataIndex: 'isRequired',
                      key: 'isRequired',
                      render: (isRequired: boolean) => (
                        <Tag color={isRequired ? 'red' : 'green'}>
                          {isRequired ? 'Required' : 'Optional'}
                        </Tag>
                      ),
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_: any, record: FileRequirement, index: number) => (
                        <Space>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditFileRequirement(record, index)}
                          />
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteFileRequirement(index)}
                          />
                        </Space>
                      ),
                    },
                  ]}
                  rowKey="name"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>

        </>
      )}

      {/* Custom Field Modal */}
      <Modal
        title={editingCustomField ? 'Edit Custom Field' : 'Add Custom Field'}
        visible={customFieldModalVisible}
        onOk={handleSaveCustomField}
        onCancel={() => {
          setCustomFieldModalVisible(false);
          customFieldForm.resetFields();
          setEditingCustomField(null);
        }}
        width={600}
      >
        <Form form={customFieldForm} layout="vertical">
          <Form.Item
            name="name"
            label="Field Name"
            rules={[
              { required: true, message: 'Please enter field name' },
              { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: 'Field name must be a valid identifier' }
            ]}
          >
            <Input placeholder="e.g., additional_requirements" />
          </Form.Item>

          <Form.Item
            name="label"
            label="Field Label"
            rules={[{ required: true, message: 'Please enter field label' }]}
          >
            <Input placeholder="e.g., Additional Requirements" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Field Type"
            rules={[{ required: true, message: 'Please select field type' }]}
          >
            <Select placeholder="Select field type">
              <Option value="text">Text</Option>
              <Option value="textarea">Text Area</Option>
              <Option value="number">Number</Option>
              <Option value="date">Date</Option>
              <Option value="select">Select</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="isRequired"
            label="Required Field"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* File Requirement Modal */}
      <Modal
        title={editingFileRequirement ? 'Edit File Requirement' : 'Add File Requirement'}
        visible={fileRequirementModalVisible}
        onOk={handleSaveFileRequirement}
        onCancel={() => {
          setFileRequirementModalVisible(false);
          fileRequirementForm.resetFields();
          setEditingFileRequirement(null);
        }}
        width={600}
      >
        <Form form={fileRequirementForm} layout="vertical">
          <Form.Item
            name="name"
            label="Document Name"
            rules={[
              { required: true, message: 'Please enter document name' },
              { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: 'Document name must be a valid identifier' }
            ]}
          >
            <Input placeholder="e.g., additional_certification" />
          </Form.Item>

          <Form.Item
            name="label"
            label="Document Label"
            rules={[{ required: true, message: 'Please enter document label' }]}
          >
            <Input placeholder="e.g., Additional Certification" />
          </Form.Item>

          <Form.Item
            name="documentType"
            label="Document Type"
            rules={[{ required: true, message: 'Please select document type' }]}
          >
            <Select placeholder="Select document type">
              <Option value="license">License</Option>
              <Option value="certificate">Certificate</Option>
              <Option value="proposal">Proposal</Option>
              <Option value="profile">Company Profile</Option>
              <Option value="financial">Financial Document</Option>
              <Option value="technical">Technical Document</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              placeholder="Describe what this document should contain"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="isRequired"
            label="Required Document"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};